import { api } from '../utils/data-utils';

export type MembershipSummary = {
  tenant: string;
  role: string;
  status: string;
  approvalGroup: string;
};

export type MeResponse = {
  email: string;
  sub: string;
  isSuperAdmin: boolean;
  memberships: MembershipSummary[];
  currentTenant: {
    tenant: string;
    allowed: boolean;
    reason: 'not_member' | null;
  };
};

/** One row of a tenant's membership table, as the membership API reports it. */
export type TenantMember = {
  email: string;
  tenant: string;
  role: string;
  status: string;
  /** The stored `¬`-joined string; prefer `approvalGroups`. */
  approvalGroup: string;
  /** Reward only: the functions this person approves for, plus any special groups. */
  approvalGroups: string[];
  source: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  bound: boolean;
  boundAt: string;
};

export type MembersResponse = {
  tenant: string;
  members: TenantMember[];
  /** Reward only: every group in use plus the special ones, for pickers. Empty elsewhere. */
  approvalGroupOptions?: string[];
};
export type MemberResponse = { tenant: string; member: TenantMember };
export type MemberInvite = { email: string; role?: string; approvalGroup?: string };
/** A key left out leaves that attribute alone; an empty approvalGroups list removes every group. */
export type MemberPatch = { role?: string; approvalGroups?: string[] };

export const MEMBER_ROLES = ['member', 'admin'] as const;
export const MEMBER_ROLE_LABELS: Record<string, string> = { member: 'Member', admin: 'Admin' };
/** Approval groups route reward approvals; no other tenant has them. */
export const APPROVAL_GROUP_TENANT = 'reward';
/**
 * Groups with a meaning beyond "approves for this function": Administrator
 * manages every reward function's members and approvals; cos-approver takes
 * the Chief Operating Officer approval stage.
 */
export const SPECIAL_APPROVAL_GROUPS: Record<string, string> = {
  administrator: 'Administrator (approves for every function)',
  'cos-approver': 'COS approver',
};

export const approvalGroupLabel = (group: string): string =>
  SPECIAL_APPROVAL_GROUPS[group.trim().toLowerCase()] || group;

/**
 * Whether the signed-in person may manage `tenant`'s members: super admins
 * may on every tenant, anyone else needs the admin role on that tenant.
 */
export const canManageMembers = (me: MeResponse | undefined, tenant?: string | null): boolean => {
  if (!me || !tenant) return false;
  if (me.isSuperAdmin) return true;
  return me.memberships.some(
    (membership) => membership.tenant === tenant && membership.role === 'admin',
  );
};

/** Who the signed-in user is and which tenants admit them. Sends X-Tenant for the page's tenant. */
export const fetchMe = async (token: string, tenant?: string | null): Promise<MeResponse> => {
  const res = await api(token, '/membership/me', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...(tenant ? { 'X-Tenant': tenant } : {}),
    },
  });
  return (await res.json()) as MeResponse;
};

const headersFor = (tenant: string) => ({ 'Content-Type': 'application/json', 'X-Tenant': tenant });

const membersPath = (tenant: string, email?: string) =>
  `/membership/${encodeURIComponent(tenant)}/members${email ? `/${encodeURIComponent(email)}` : ''}`;

export const listMembers = async (token: string, tenant: string): Promise<MembersResponse> => {
  const res = await api(token, membersPath(tenant), { method: 'GET', headers: headersFor(tenant) });
  return (await res.json()) as MembersResponse;
};

export const inviteMembers = async (
  token: string,
  tenant: string,
  members: MemberInvite[],
): Promise<MembersResponse> => {
  const res = await api(token, membersPath(tenant), {
    method: 'PUT',
    headers: headersFor(tenant),
    body: JSON.stringify({ members }),
  });
  return (await res.json()) as MembersResponse;
};

export const updateMember = async (
  token: string,
  tenant: string,
  email: string,
  patch: MemberPatch,
): Promise<MemberResponse> => {
  const res = await api(token, membersPath(tenant, email), {
    method: 'PATCH',
    headers: headersFor(tenant),
    body: JSON.stringify(patch),
  });
  return (await res.json()) as MemberResponse;
};

export const removeMember = async (token: string, tenant: string, email: string): Promise<void> => {
  await api(token, membersPath(tenant, email), { method: 'DELETE', headers: headersFor(tenant) });
};
